import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { getApiUrl } from '@/lib/config';
import { useCreateChannel } from '../../hooks';
import { useMetaEmbeddedSignup } from '../../hooks/useMetaEmbeddedSignup';
import { Icon } from '@/shared/icons';
import { TokenStatusAlert, type TokenInfo } from './TokenStatusAlert';

const META_GRAPH_API_VERSION = 'v23.0';
const META_GRAPH_API_BASE = `https://graph.facebook.com/${META_GRAPH_API_VERSION}`;

interface MetaBusinessOption {
  id: string;
  name: string;
}

interface MetaPhoneOption {
  wabaId: string;
  wabaName: string;
  phoneId: string;
  displayPhoneNumber: string;
  verifiedName?: string;
  status?: string;
  verificationStatus?: string;
}

const REQUIRED_WHATSAPP_SCOPES = [
  'whatsapp_business_management',
  'whatsapp_business_messaging',
];

async function fetchTokenInfo(token: string, requiredScopes: string[]): Promise<TokenInfo | null> {
  try {
    const url = `${META_GRAPH_API_BASE}/debug_token?input_token=${encodeURIComponent(token)}&access_token=${encodeURIComponent(token)}`;
    const res = await fetch(url);
    const data = await res.json();
    if (data.error || !data.data) return null;
    const scopes: string[] = data.data.scopes || [];
    const missingScopes = requiredScopes.filter((s) => !scopes.includes(s));
    return {
      expiresAt: typeof data.data.expires_at === 'number' ? data.data.expires_at : 0,
      scopes,
      isValid: data.data.is_valid !== false,
      missingScopes,
    };
  } catch {
    return null;
  }
}
/**
 * Form for connecting WhatsApp Business API
 *
 * Flow: Collect tokens → Test connection → Create channel → Show webhook URL
 *
 * @example
 * <WhatsAppBusinessForm
 *   onComplete={(data) => handleAuthComplete(data)}
 * />
 */
export interface WhatsAppBusinessFormProps {
  /** Callback when authentication is complete */
  onComplete: (data?: WhatsAppBusinessData) => void;
}

export interface WhatsAppBusinessData {
  providerId: string;
  name: string;
  identifier: string;
  config?: {
    phoneNumberId?: string;
    whatsappBusinessAccountId?: string;
    webhookToken?: string;
  };
  credentials: {
    accessToken: string;
    phoneNumberId: string;
    businessAccountId: string;
  };
}

interface ModeCardProps {
  selected: boolean;
  onClick: () => void;
  badge?: string;
  iconEmoji: string;
  title: string;
  subtitle: string;
  bullets: string[];
  testId: string;
}

const ModeCard: React.FC<ModeCardProps> = ({ selected, onClick, badge, iconEmoji, title, subtitle, bullets, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex-1 p-4 rounded-lg text-left transition-all"
    style={{
      backgroundColor: 'var(--theme-card-background)',
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-card-border)',
      boxShadow: selected ? '0 0 0 3px rgba(139, 92, 246, 0.1)' : 'none',
      cursor: 'pointer',
    }}
    data-testid={testId}
    data-selected={selected ? 'true' : 'false'}
  >
    <div className="flex items-start gap-2 mb-2">
      <div
        className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-card-border)' }}
      >
        {selected && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: 'var(--theme-primary)' }} />}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--theme-card-text)' }}>{title}</span>
          {badge && (
            <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: 'var(--theme-primary)', color: 'white' }}>
              {badge}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--theme-card-muted)', marginTop: 2 }}>{iconEmoji} {subtitle}</div>
      </div>
    </div>
    <ul className="space-y-1 ml-7">
      {bullets.map((b, i) => (
        <li key={i} style={{ fontSize: 12, color: 'var(--theme-card-muted)' }}>• {b}</li>
      ))}
    </ul>
  </button>
);

type ConnectionStep = 'credentials' | 'connecting' | 'webhook' | 'complete';
type AuthMode = 'manual' | 'embedded';
type ConnectionMode = 'standalone' | 'coexistence';

export const WhatsAppBusinessForm: React.FC<WhatsAppBusinessFormProps> = ({
  onComplete,
}) => {
  const [step, setStep] = useState<ConnectionStep>('credentials');
  const [authMode, setAuthMode] = useState<AuthMode>('embedded');
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>('standalone');
  const [coexistenceAvailable, setCoexistenceAvailable] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [businessAccountId, setBusinessAccountId] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Status do setup automático (embedded): webhook assinado + número registrado
  // pelo backend durante o POST /channels — lido de channel.integration após criar
  const [setupStatus, setSetupStatus] = useState<{
    webhookSubscribed?: boolean;
    phoneRegistered?: boolean;
    setupError?: string;
  } | null>(null);

  const createChannel = useCreateChannel();

  // Meta Embedded Signup hook
  const {
    startEmbeddedSignup,
    isLoading: isEmbeddedLoading,
    error: embeddedError,
    result: embeddedResult,
    reset: resetEmbedded,
  } = useMetaEmbeddedSignup();

  // Manual mode — Graph API discovery state
  const [isConnectingToken, setIsConnectingToken] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [businesses, setBusinesses] = useState<MetaBusinessOption[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [isLoadingPhones, setIsLoadingPhones] = useState(false);
  const [phonesError, setPhonesError] = useState<string | null>(null);
  const [phones, setPhones] = useState<MetaPhoneOption[]>([]);
  const [selectedPhoneKey, setSelectedPhoneKey] = useState(''); // formato: `${wabaId}|${phoneId}`
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);

  // Handle Embedded Signup result - auto-create channel
  useEffect(() => {
    if (embeddedResult) {
      // Auto-fill credentials from embedded signup
      setAccessToken(embeddedResult.accessToken);
      setPhoneNumberId(embeddedResult.phoneNumberId);
      setBusinessAccountId(embeddedResult.wabaId);

      // Auto-create channel with embedded credentials
      handleEmbeddedChannelCreate(embeddedResult);
    }
  }, [embeddedResult]);

  // Gate administrativo: o picker (standalone vs coexistência) só aparece quando
  // o admin habilita `coexistenceEnabled=true` na app-integration via TROIA-V3-ADMIN.
  // Meta usa o MESMO configId para os dois modos — a diferença vem do featureType
  // em runtime, não de um config separado.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { metaApi } = await import('../../services/metaApi');
        const cfg = await metaApi.getEmbeddedSignupConfig();
        if (!cancelled) setCoexistenceAvailable(cfg.coexistenceEnabled === true);
      } catch {
        // Silencia — se a config falha aqui, o botão de login vai falhar com mensagem clara depois.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEmbeddedChannelCreate = async (result: typeof embeddedResult) => {
    if (!result) return;

    // Use channel name from input, or fallback to the display phone number
    const autoChannelName = channelName.trim() || result.displayPhoneNumber || result.phoneNumberId;

    setStep('connecting');

    try {
      const channelRequest = {
        name: autoChannelName,
        identifier: result.displayPhoneNumber || result.phoneNumberId,
        providerId: 'whatsapp-business',
        config: {
          phoneNumberId: result.phoneNumberId,
          whatsappBusinessAccountId: result.wabaId,
          webhookToken: 'auto-generated',
          webhookConfigured: false,
          ...(result.mode !== undefined && { mode: result.mode }),
          ...(result.accountMode !== undefined && { accountMode: result.accountMode }),
          ...(result.accountMode !== undefined && { accountModeUpdatedAt: new Date().toISOString() }),
        },
        credentials: {
          accessToken: result.accessToken,
          phoneNumberId: result.phoneNumberId,
          businessAccountId: result.wabaId,
          fromEmbeddedSignup: true,
        },
        integrationName: `${autoChannelName} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      const channel = await createChannel.mutateAsync(channelRequest);

      // Build webhook URL com baseURL do backend (não do frontend)
      const integrationId = channel.integrationId;
      setWebhookUrl(`${getApiUrl()}/api/provider/webhook/${integrationId}`);

      // O backend assinou o webhook e registrou o número automaticamente durante o
      // POST /channels — buscar o status persistido pra exibir na tela final
      // (nada é manual no fluxo embedded; instruções manuais são só do fluxo manual)
      try {
        const { channelsApi } = await import('../../services/channelsApi');
        const freshChannel = await channelsApi.getById(channel.id);
        setSetupStatus({
          ...(freshChannel.integration?.webhookSubscribed !== undefined && { webhookSubscribed: freshChannel.integration.webhookSubscribed }),
          ...(freshChannel.integration?.phoneRegistered !== undefined && { phoneRegistered: freshChannel.integration.phoneRegistered }),
          ...(freshChannel.integration?.setupError !== undefined && { setupError: freshChannel.integration.setupError }),
        });
      } catch {
        setSetupStatus(null); // Tela mostra estado neutro — canal foi criado de qualquer forma
      }

      setStep('webhook');
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
      if (axiosError.response?.status === 403) return; // Handled by global interceptor (toast)
      if (axiosError.response?.status === 402) return; // Handled by global interceptor (toast)
      const errorMessage = axiosError.response?.data?.message
        || (error instanceof Error ? error.message : 'Erro ao criar canal. Tente novamente.');
      console.error('Erro ao criar canal via Embedded Signup:', error);
      setErrorMessage(errorMessage);
      setStep('credentials');
      resetEmbedded();
    }
  };

  const resetManualDiscovery = () => {
    setBusinesses([]);
    setSelectedBusinessId('');
    setPhones([]);
    setSelectedPhoneKey('');
    setBusinessAccountId('');
    setPhoneNumberId('');
    setTokenError(null);
    setPhonesError(null);
    setTokenInfo(null);
  };

  const handleConnectToken = async () => {
    if (!accessToken.trim()) {
      setTokenError('Cole seu Access Token primeiro');
      return;
    }

    setIsConnectingToken(true);
    setTokenError(null);
    resetManualDiscovery();

    try {
      const url = `${META_GRAPH_API_BASE}/me?fields=businesses{id,name}&access_token=${encodeURIComponent(accessToken.trim())}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.error) {
        setTokenError(data.error.message || 'Token inválido');
        return;
      }

      const businessesList: MetaBusinessOption[] = (data.businesses?.data || []).map((b: any) => ({
        id: b.id,
        name: b.name,
      }));

      if (businessesList.length === 0) {
        setTokenError('Nenhuma conta de negócios encontrada para este token');
        return;
      }

      setBusinesses(businessesList);

      // Buscar metadata do token (expiração + scopes) em paralelo, sem bloquear
      const info = await fetchTokenInfo(accessToken.trim(), REQUIRED_WHATSAPP_SCOPES);
      setTokenInfo(info);

      if (businessesList.length === 1) {
        await handleSelectBusiness(businessesList[0]!.id);
      }
    } catch (err) {
      setTokenError(err instanceof Error ? err.message : 'Erro ao conectar com o Meta');
    } finally {
      setIsConnectingToken(false);
    }
  };

  const handleSelectBusiness = async (businessId: string) => {
    setSelectedBusinessId(businessId);
    setSelectedPhoneKey('');
    setPhones([]);
    setBusinessAccountId('');
    setPhoneNumberId('');
    setPhonesError(null);

    if (!businessId) return;

    setIsLoadingPhones(true);
    try {
      const url = `${META_GRAPH_API_BASE}/${businessId}?fields=owned_whatsapp_business_accounts{id,name,phone_numbers{id,display_phone_number,verified_name,status,verification_status}}&access_token=${encodeURIComponent(accessToken.trim())}`;
      const res = await fetch(url);
      const data = await res.json();

      if (data.error) {
        setPhonesError(data.error.message || 'Erro ao buscar números');
        return;
      }

      const wabas = data.owned_whatsapp_business_accounts?.data || [];
      const allPhones: MetaPhoneOption[] = [];

      wabas.forEach((waba: any) => {
        const wabaName = waba.name || 'WhatsApp Business';
        const phoneNumbers = waba.phone_numbers?.data || [];
        phoneNumbers.forEach((phone: any) => {
          allPhones.push({
            wabaId: waba.id,
            wabaName,
            phoneId: phone.id,
            displayPhoneNumber: phone.display_phone_number || phone.id,
            verifiedName: phone.verified_name,
            status: phone.status,
            verificationStatus: phone.verification_status,
          });
        });
      });

      if (allPhones.length === 0) {
        setPhonesError('Nenhum número WhatsApp configurado nesta conta. Configure um número no Meta Business primeiro.');
        return;
      }

      setPhones(allPhones);

      if (allPhones.length === 1) {
        const only = allPhones[0]!;
        handleSelectPhone(`${only.wabaId}|${only.phoneId}`);
      }
    } catch (err) {
      setPhonesError(err instanceof Error ? err.message : 'Erro ao buscar números');
    } finally {
      setIsLoadingPhones(false);
    }
  };

  const handleSelectPhone = (key: string) => {
    setSelectedPhoneKey(key);
    if (!key) {
      setBusinessAccountId('');
      setPhoneNumberId('');
      return;
    }
    const [wabaId, phoneId] = key.split('|');
    if (!wabaId || !phoneId) return;
    setBusinessAccountId(wabaId);
    setPhoneNumberId(phoneId);

    // Auto-preencher o nome do canal se ainda estiver vazio
    if (!channelName.trim()) {
      const phone = phones.find((p) => p.wabaId === wabaId && p.phoneId === phoneId);
      if (phone) {
        setChannelName(`WhatsApp ${phone.displayPhoneNumber}`);
      }
    }
  };

  const handleConnect = async () => {
    if (!accessToken.trim() || !phoneNumberId.trim() || !businessAccountId.trim()) {
      setErrorMessage('Selecione um Business Account e um número de telefone');
      return;
    }

    const finalName = channelName.trim() || `WhatsApp ${phoneNumberId}`;
    if (finalName.length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    setErrorMessage(null);
    setStep('connecting');

    try {
      const channelRequest = {
        name: finalName,
        identifier: phoneNumberId.trim(),
        providerId: 'whatsapp-business',
        config: {
          phoneNumberId,
          whatsappBusinessAccountId: businessAccountId,
          webhookToken: 'auto-generated'
        },
        credentials: {
          accessToken,
          phoneNumberId,
          businessAccountId
        },
        integrationName: `${finalName} Integration`,
        assignmentConfig: {
          enabled: true,
          type: 'sequential' as const,
        },
      };

      // Call backend API to create channel
      const channel = await createChannel.mutateAsync(channelRequest);

      // Build webhook URL com baseURL do backend (não do frontend)
      const integrationId = channel.integrationId;
      setWebhookUrl(`${getApiUrl()}/api/provider/webhook/${integrationId}`);

      setStep('webhook');
    } catch (error: any) {
      if (error.response?.status === 403) return; // Handled by global interceptor (toast)
      if (error.response?.status === 402) return; // Handled by global interceptor (toast)
      console.error('❌ Erro ao criar canal:', error);
      setErrorMessage(error.response?.data?.message || 'Erro ao criar canal. Tente novamente.');
      setStep('credentials');
    }
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    // TODO: Show toast notification
  };

  const handleComplete = () => {
    // Canal já criado em handleConnect / handleEmbeddedChannelCreate — apenas fecha
    onComplete();
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Credentials Input */}
      {step === 'credentials' && (
        <div className="space-y-4">
          {/* Auth Mode Selector */}
          <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--theme-card-border)' }}>
            <button
              type="button"
              onClick={() => setAuthMode('embedded')}
              className="flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              style={{
                backgroundColor: authMode === 'embedded' ? 'var(--theme-button-primary)' : 'transparent',
                color: authMode === 'embedded' ? 'white' : 'var(--theme-card-text)',
              }}
            >
              <Icon name="facebook" size={18} />
              Login com Facebook
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('manual')}
              className="flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              style={{
                backgroundColor: authMode === 'manual' ? 'var(--theme-button-primary)' : 'transparent',
                color: authMode === 'manual' ? 'white' : 'var(--theme-card-text)',
              }}
            >
              <Icon name="key" size={18} />
              Credenciais Manuais
            </button>
          </div>

          {/* Embedded Signup Mode */}
          {authMode === 'embedded' && (
            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome do Canal (opcional)
                </label>
                <Input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="Ex: WhatsApp Business Suporte"
                  className="w-full"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Se não informado, será usado "WhatsApp Business"
                </p>
              </div>

              {/* Mode picker — só quando admin habilitou coexistence */}
              {coexistenceAvailable ? (
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--theme-card-text)' }}
                  >
                    Como você quer conectar o número?
                  </label>
                  <div className="flex gap-3">
                    <ModeCard
                      selected={connectionMode === 'standalone'}
                      onClick={() => setConnectionMode('standalone')}
                      badge="Recomendado"
                      iconEmoji="📱→💻"
                      title="Exclusivo na Cloud API"
                      subtitle="Migra o número pra cá"
                      bullets={[
                        'Número sai do WhatsApp Business App do celular',
                        'Todos os recursos da Cloud API liberados',
                        'Templates de marketing disponíveis no dia 1',
                      ]}
                      testId="whatsapp-mode-card-standalone"
                    />
                    <ModeCard
                      selected={connectionMode === 'coexistence'}
                      onClick={() => setConnectionMode('coexistence')}
                      iconEmoji="📱+💻"
                      title="Manter app no celular"
                      subtitle="Coexistência"
                      bullets={[
                        'Continua usando o app no celular normalmente',
                        'Sincroniza últimas mensagens automaticamente',
                        'Templates Marketing só após 14 dias',
                      ]}
                      testId="whatsapp-mode-card-coexistence"
                    />
                  </div>
                </div>
              ) : (
                /* Info box original — preservado quando coexistence não está habilitado */
                <div
                  className="p-4 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-info-background)',
                    border: '1px solid var(--theme-info-color)',
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Icon name="facebook" size={24}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: '#1877F2' }} />
                    <div>
                      <p
                        className="text-sm font-medium mb-1"
                        style={{ color: 'var(--theme-card-text)' }}
                      >
                        Configuração rápida via Facebook
                      </p>
                      <p
                        className="text-sm"
                        style={{ color: 'var(--theme-card-muted)' }}
                      >
                        Conecte sua conta do WhatsApp Business diretamente pelo Facebook.
                        O processo é mais rápido e as credenciais são configuradas automaticamente.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Embedded Error */}
              {embeddedError && (
                <div
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                >
                  <div className="flex items-start gap-2">
                    <Icon name="alert-circle" size={18}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: 'var(--theme-error-color)' }} />
                    <p
                      className="text-sm"
                      style={{ color: 'var(--theme-error-color)' }}
                    >
                      {embeddedError}
                    </p>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                >
                  <p
                    className="text-sm"
                    style={{ color: 'var(--theme-error-color)' }}
                  >
                    {errorMessage}
                  </p>
                </div>
              )}

              {/* Facebook Login Button */}
              <Button
                onClick={() => startEmbeddedSignup({ mode: connectionMode })}
                disabled={isEmbeddedLoading}
                className="w-full"
                style={{
                  backgroundColor: '#1877F2',
                  color: 'white',
                }}
                data-testid="whatsapp-form-embedded-submit"
              >
                {isEmbeddedLoading ? (
                  <>Conectando...</>
                ) : (
                  <>
                    <Icon name="facebook" size={18} className="mr-2" />
                    Continuar com Facebook
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Manual Mode */}
          {authMode === 'manual' && (
            <div className="space-y-4">
              {/* Nome do Canal (opcional, auto-preenchido após selecionar número) */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome do Canal
                </label>
                <Input
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="Ex: WhatsApp Business Suporte"
                  className="w-full"
                  data-testid="whatsapp-form-channel-name"
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Se não informado, será gerado a partir do número selecionado
                </p>
              </div>

              {/* Access Token + botão Conectar grudado à direita */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Access Token *
                </label>
                <div className="flex items-stretch gap-0">
                  <div className="relative flex-1">
                    <Icon name="key" size={18}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                      style={{ color: 'var(--theme-card-muted)' }} />
                    <Input
                      type="password"
                      value={accessToken}
                      onChange={(e) => {
                        setAccessToken(e.target.value);
                        if (businesses.length > 0) resetManualDiscovery();
                      }}
                      placeholder="EAAxxxxxxxxxxxxxxx"
                      className="w-full pl-10"
                      style={{
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        borderRight: 'none',
                      }}
                      data-testid="whatsapp-form-access-token"
                    />
                  </div>
                  <Button
                    onClick={handleConnectToken}
                    disabled={isConnectingToken || !accessToken.trim()}
                    className="flex-shrink-0"
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      height: '36px',
                    }}
                    data-testid="whatsapp-form-connect-token-button"
                  >
                    {isConnectingToken ? (
                      <>
                        <Icon name="loader" size={14} className="mr-1.5 animate-spin" />
                        Conectando...
                      </>
                    ) : businesses.length > 0 ? (
                      <>
                        <Icon name="refresh" size={14} className="mr-1.5" />
                        Reconectar
                      </>
                    ) : (
                      <>Conectar</>
                    )}
                  </Button>
                </div>
                <p
                  className="text-xs mt-1"
                  style={{ color: 'var(--theme-card-muted)' }}
                >
                  Cole o token gerado no <a
                    href="https://developers.facebook.com/tools/explorer/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: 'var(--theme-info-color)' }}
                  >Graph API Explorer</a> e clique em Conectar
                </p>

                {tokenError && (
                  <div
                    className="mt-2 p-2.5 rounded-lg flex items-start gap-2"
                    style={{
                      backgroundColor: 'var(--theme-error-background)',
                      border: '1px solid var(--theme-error-color)',
                    }}
                    data-testid="whatsapp-form-token-error"
                  >
                    <Icon name="alert-circle" size={16}
                      className="flex-shrink-0 mt-0.5"
                      style={{ color: 'var(--theme-error-color)' }} />
                    <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                      {tokenError}
                    </p>
                  </div>
                )}
              </div>

              {/* Alerta de expiração / scopes faltantes */}
              {tokenInfo && (
                <TokenStatusAlert
                  info={tokenInfo}
                  testIdPrefix="whatsapp-form"
                  accessToken={accessToken}
                />
              )}

              {/* Business Account ID select — visível após Conectar com sucesso */}
              {businesses.length > 0 && (
                <Select
                  label="Business Account ID"
                  required
                  options={businesses.map((b) => ({
                    value: b.id,
                    label: `${b.name} (${b.id})`,
                  }))}
                  value={selectedBusinessId}
                  onChange={handleSelectBusiness}
                  placeholder="Selecione uma conta de negócios"
                  data-testid="whatsapp-form-business-select"
                />
              )}

              {/* Estados do segundo passo */}
              {selectedBusinessId && isLoadingPhones && (
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--theme-card-muted)' }}>
                  <Icon name="loader" size={14} className="animate-spin" />
                  Buscando números disponíveis...
                </div>
              )}

              {phonesError && (
                <div
                  className="p-3 rounded-lg flex items-start gap-2"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                  data-testid="whatsapp-form-phones-error"
                >
                  <Icon name="alert-circle" size={18}
                    className="flex-shrink-0 mt-0.5"
                    style={{ color: 'var(--theme-error-color)' }} />
                  <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                    {phonesError}
                  </p>
                </div>
              )}

              {/* Phone Number ID select — visível após selecionar Business e ter números */}
              {phones.length > 0 && (
                <Select
                  label="Phone Number ID"
                  required
                  options={phones.map((p) => {
                    const verified = p.verificationStatus === 'VERIFIED' || p.status === 'CONNECTED';
                    const statusIcon = verified ? '✅' : '⚠️';
                    const statusLabel = verified ? 'Verificado' : 'Pendente';
                    return {
                      value: `${p.wabaId}|${p.phoneId}`,
                      label: `${statusIcon} ${p.displayPhoneNumber} — ${p.wabaName} (${statusLabel})`,
                    };
                  })}
                  value={selectedPhoneKey}
                  onChange={handleSelectPhone}
                  placeholder="Selecione um número"
                  data-testid="whatsapp-form-phone-select"
                />
              )}

              {errorMessage && (
                <div
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--theme-error-background)',
                    border: '1px solid var(--theme-error-color)',
                  }}
                  data-testid="whatsapp-form-error"
                >
                  <p className="text-sm" style={{ color: 'var(--theme-error-color)' }}>
                    {errorMessage}
                  </p>
                </div>
              )}

              <Button
                onClick={handleConnect}
                className="w-full"
                disabled={!businessAccountId || !phoneNumberId}
                data-testid="whatsapp-form-submit"
              >
                {!businessAccountId || !phoneNumberId
                  ? 'Selecione um número para continuar'
                  : 'Conectar WhatsApp Business'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Connecting */}
      {step === 'connecting' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center animate-pulse"
              style={{ backgroundColor: 'var(--theme-button-primary)' }}
            >
              <Icon name="key" size={32} className="text-white" />
            </div>
          </div>
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Conectando ao WhatsApp Business...
          </p>
          <p
            className="text-sm mt-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Validando credenciais e configurando webhook
          </p>
        </div>
      )}

      {/* Step 3 (embedded): setup automático — webhook e registro já feitos pelo backend */}
      {step === 'webhook' && authMode === 'embedded' && (
        <div className="space-y-4" data-testid="whatsapp-form-setup-status">
          <div
            className="p-4 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              border: '1px solid var(--theme-success-color)',
            }}
          >
            <Icon name="check-circle" size={20}
              style={{ color: 'var(--theme-success-color)' }}
              className="flex-shrink-0 mt-0.5" />
            <div>
              <p
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Canal criado via Facebook!
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Webhook e registro do número foram configurados automaticamente — nada precisa ser feito no painel da Meta.
              </p>
            </div>
          </div>

          <div
            className="p-4 rounded-lg space-y-3"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
              Status da configuração automática:
            </p>
            <div className="flex items-center gap-2" data-testid="whatsapp-form-setup-webhook">
              <Icon
                name={setupStatus?.webhookSubscribed === false ? 'x-circle' : 'check-circle'}
                size={16}
                style={{ color: setupStatus?.webhookSubscribed === false ? 'var(--theme-error-color)' : 'var(--theme-success-color)' }}
              />
              <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                Webhook assinado (recebimento de mensagens)
              </span>
            </div>
            <div className="flex items-center gap-2" data-testid="whatsapp-form-setup-register">
              <Icon
                name={setupStatus?.phoneRegistered === false ? 'x-circle' : 'check-circle'}
                size={16}
                style={{ color: setupStatus?.phoneRegistered === false ? 'var(--theme-error-color)' : 'var(--theme-success-color)' }}
              />
              <span className="text-sm" style={{ color: 'var(--theme-card-text)' }}>
                Número registrado na Cloud API (envio de mensagens)
              </span>
            </div>
            {setupStatus?.setupError && (
              <div
                className="p-3 rounded-lg text-sm"
                data-testid="whatsapp-form-setup-error"
                style={{
                  backgroundColor: 'var(--theme-error-background)',
                  border: '1px solid var(--theme-error-color)',
                  color: 'var(--theme-card-text)',
                }}
              >
                <p className="font-medium">Um passo do setup falhou:</p>
                <p className="mt-1" style={{ color: 'var(--theme-card-muted)' }}>{setupStatus.setupError}</p>
                <p className="mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                  Use "Reconectar" no card do canal para tentar novamente.
                </p>
              </div>
            )}
          </div>

          <div
            className="p-4 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <Icon name="credit-card" size={18}
              style={{ color: 'var(--theme-info-color)' }}
              className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                Último passo (no WhatsApp Manager): método de pagamento
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--theme-card-muted)' }}>
                Para enviar mensagens cobradas pela Meta, adicione um método de pagamento na conta do WhatsApp Business.
              </p>
              <a
                href="https://business.facebook.com/wa/manage/home/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm flex items-center gap-1 hover:underline mt-2"
                style={{ color: 'var(--theme-info-color)' }}
              >
                Abrir WhatsApp Manager
                <Icon name="external-link" size={14} />
              </a>
            </div>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full"
            data-testid="whatsapp-form-complete-button"
          >
            Concluir Configuração
          </Button>
        </div>
      )}

      {/* Step 3 (manual): Show Webhook URL — cliente configura no próprio app Meta dele */}
      {step === 'webhook' && authMode === 'manual' && (
        <div className="space-y-4">
          <div
            className="p-4 rounded-lg flex items-start gap-3"
            style={{
              backgroundColor: 'var(--theme-success-background)',
              border: '1px solid var(--theme-success-color)',
            }}
          >
            <Icon name="check-circle" size={20}
              style={{ color: 'var(--theme-success-color)' }}
              className="flex-shrink-0 mt-0.5" />
            <div>
              <p
                className="font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Credenciais validadas com sucesso!
              </p>
              <p
                className="text-sm mt-1"
                style={{ color: 'var(--theme-card-muted)' }}
              >
                Agora configure o webhook no Meta Business
              </p>
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              URL do Webhook
            </label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  color: 'var(--theme-card-muted)',
                }}
              />
              <Button
                onClick={handleCopyWebhook}
                variant="secondary"
                className="flex-shrink-0"
              >
                <Icon name="copy" size={16} />
              </Button>
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Copie esta URL e configure no Meta Business Manager
            </p>
          </div>

          <div
            className="p-4 rounded-lg space-y-2"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <p
              className="text-sm font-medium"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Como configurar o webhook:
            </p>
            <ol
              className="text-sm space-y-1 list-decimal list-inside"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              <li>Acesse o Meta Business Manager</li>
              <li>Vá em WhatsApp &gt; Configuração &gt; Webhook</li>
              <li>Cole a URL do webhook acima</li>
              <li>Configure o token de verificação (se solicitado)</li>
              <li>Ative os eventos de mensagem</li>
            </ol>
            <a
              href="https://developers.facebook.com/docs/whatsapp/cloud-api/guides/set-up-webhooks"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex items-center gap-1 hover:underline mt-2"
              style={{ color: 'var(--theme-info-color)' }}
            >
              Ver documentação completa
              <Icon name="external-link" size={14} />
            </a>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full"
            data-testid="whatsapp-form-complete-button"
          >
            Concluir Configuração
          </Button>
        </div>
      )}
    </div>
  );
};

WhatsAppBusinessForm.displayName = 'WhatsAppBusinessForm';
